import { Component, Input, forwardRef, inject, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import { CurrencyDefinitionRecord, FinanceApiService } from '../../features/finance/finance-api.service';

interface CurrencyOption {
  code: string;
  name: string;
  label: string;
  isActive: boolean;
}

@Component({
  standalone: true,
  selector: 'ain-currency-selector',
  imports: [FormsModule, SelectModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CurrencySelectorComponent),
      multi: true
    }
  ],
  template: `
    <p-select
      class="selector"
      [options]="options()"
      optionLabel="label"
      optionValue="code"
      [placeholder]="t(placeholderKey)"
      [showClear]="true"
      [filter]="showSearch"
      filterBy="label,code,name"
      [disabled]="disabled || loading()"
      [loading]="loading()"
      [ngModel]="value()"
      (ngModelChange)="onValueChange($event)"
      [appendTo]="appendTo" />
  `,
  styles: [`
    .selector { width: 100%; }
  `]
})
export class CurrencySelectorComponent implements ControlValueAccessor {
  private readonly api = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  @Input() placeholderKey = 'finance.currencies.selectPlaceholder';
  @Input() activeOnly = true;
  @Input() showSearch = false;
  @Input() appendTo: 'body' | 'self' = 'body';

  readonly loading = signal(false);
  readonly options = signal<CurrencyOption[]>([]);
  readonly value = signal<string | null>(null);

  private readonly allOptions = signal<CurrencyOption[]>([]);
  disabled = false;

  constructor() {
    this.load();
  }

  writeValue(value: string | null): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  onValueChange(value: string | null): void {
    this.value.set(value);
    this.onChange(value);
    this.onTouched();
  }

  private load(): void {
    this.loading.set(true);
    this.api.listCurrencies().subscribe({
      next: rows => {
        this.allOptions.set(rows.map(item => this.toOption(item)));
        this.applyFilter();
        this.loading.set(false);
      },
      error: () => {
        this.allOptions.set([]);
        this.options.set([]);
        this.loading.set(false);
      }
    });
  }

  applyFilter(): void {
    const selectedCode = this.value()?.trim().toUpperCase() ?? '';
    const all = this.allOptions();

    let filtered = all.filter(item =>
      !this.activeOnly || item.isActive);

    if (selectedCode && !filtered.some(item => item.code.toUpperCase() === selectedCode)) {
      const selected = all.find(item => item.code.toUpperCase() === selectedCode);
      if (selected) {
        filtered = [selected, ...filtered];
      }
    }

    this.options.set(filtered);
  }

  private toOption(item: CurrencyDefinitionRecord): CurrencyOption {
    return {
      code: item.code,
      name: item.name,
      label: `${item.code} - ${item.name}`,
      isActive: item.isActive
    };
  }
}
